import { useState } from 'react';
import { MailOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Typography } from 'antd';
import { useProcInstanceContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useSetProcInstanceOwner } from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { Banner, Col } from '@/shared/ui';
import { captureError } from '@/shared/utils';

const { Title, Paragraph } = Typography;

export const ProcInstanceSetOwnerBanner = () => {
  const { procInstance } = useProcInstanceContext();
  const { id = '', stepData } = procInstance || {};
  const { emailRequired } = stepData?.administration || {};
  const { showNotification } = useNotificationContext();
  const invalidate = useDataInvalidation(['instances', id]);
  const { mutate: setOwnerEmail, isLoading } = useSetProcInstanceOwner();
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(emailRequired);

  const onSubmit = ({ email }: { email: string }) => {
    setOwnerEmail(
      { pathParams: { id }, body: { email } },
      {
        onSuccess: () => {
          invalidate();
          setSendEmailModalOpen(false);
          showNotification({
            type: 'success',
            message: 'Link has been sent to email',
            description:
              'You can access the proc instance later using the link',
          });
        },
        onError: (error) => {
          if (error.message) {
            showNotification({
              type: 'error',
              message: 'Failed to send link',
              description: error.message,
            });
          }
          captureError(error, {
            module: 'proc_send_link_error',
            action: 'on_submit',
          });
        },
      },
    );
  };

  return (
    <>
      {!emailRequired && (
        <Banner
          closable
          type="info"
          message="You can access the proc instance later using the link"
          action={
            <Button
              size="small"
              type="primary"
              icon={<MailOutlined />}
              onClick={() => setSendEmailModalOpen(!sendEmailModalOpen)}
            >
              Send link
            </Button>
          }
        />
      )}
      <Modal
        title={
          <Col
            alignItems="flex-start"
            style={{ margin: '10px 0', width: '100%' }}
          >
            <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
              Send proc link to email
            </Title>
          </Col>
        }
        destroyOnClose
        closable={!emailRequired}
        keyboard={!emailRequired}
        maskClosable={!emailRequired}
        width={350}
        open={sendEmailModalOpen}
        footer={null}
        style={{ maxWidth: '90%' }}
        onCancel={() => setSendEmailModalOpen(false)}
      >
        <Paragraph style={{ marginBottom: 20 }}>
          Enter your email to receive your instance link and access the current
          proc later.
        </Paragraph>
        <Form name="sendLinkForm" onFinish={onSubmit} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item style={{ width: '100%', marginTop: 40 }}>
            <Button
              type="primary"
              loading={isLoading}
              htmlType="submit"
              style={{ width: '100%', height: 40 }}
            >
              Send link to email
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
